import { useState, useEffect, useCallback, useRef } from 'react';
import { gatewayClient } from '../services/gateway';
import { API_CONFIG } from '../services/api/config';
import type { GatewayConnectionState } from '../services/gateway';

export function useGatewayConnection(organizationId?: string, allowGlobalStatus = false) {
  const [state, setState] = useState<GatewayConnectionState>({
    connected: false,
    authOk: false,
    retryCount: 0,
  });

  const mountedRef = useRef(true);
  const isEnabled = !!organizationId || allowGlobalStatus;

  const checkConnection = useCallback(async () => {
    if (!isEnabled) {
      setState(prev => ({
        ...prev,
        connected: false,
        authOk: false,
        error: 'Gateway probing disabled',
      }));
      return;
    }

    try {
      const health = await gatewayClient.checkHealth(organizationId);
      if (!mountedRef.current) return;

      if (health.connected) {
        setState({
          connected: true,
          authOk: health.authOk,
          retryCount: 0,
          lastCheck: new Date(),
          ...(health.authOk === false && { error: health.error || 'Gateway auth failed' }),
        });
      } else {
        setState(prev => ({
          connected: false,
          authOk: false,
          error: health.error || 'Gateway unreachable',
          retryCount: prev.retryCount + 1,
          lastCheck: new Date(),
        }));
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      setState(prev => ({
        connected: false,
        authOk: false,
        error: err?.message || 'Health check failed',
        retryCount: prev.retryCount + 1,
        lastCheck: new Date(),
      }));
    }
  }, [allowGlobalStatus, isEnabled, organizationId]);

  useEffect(() => {
    mountedRef.current = true;
    checkConnection();

    const interval = setInterval(
      checkConnection,
      state.connected
        ? API_CONFIG.GATEWAY.HEALTH_INTERVAL_CONNECTED
        : API_CONFIG.GATEWAY.HEALTH_INTERVAL_DISCONNECTED,
    );

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [checkConnection, state.connected]);

  return {
    ...state,
    isGatewayConnected: state.connected && state.authOk !== false,
    checkConnection,
  };
}
